import { useMemo } from "react";
import type { RouteObject } from "react-router";
import { useNavigate } from "react-router";

import type { FormValues } from "@prequel/import-react";
import { Button, ButtonStyle, PageHeading } from "@prequel/shared-components";

import DatalakeAdmin from "./DatalakeAdmin";
import DatalakeConnection from "./DatalakeConnection";
import DatalakeDatasets from "./DatalakeDatasets";
import DatalakeDetails from "./DatalakeDetails";
import DatalakeForm from "./DatalakeForm";
import DatalakePage, { DatalakeDetailsPage, useDatalakeContext } from "./DatalakePage";
import DatalakeProviders from "./DatalakeProviders";
import DatalakesTable from "./DatalakesTable";

import Breadcrumbs from "@/components/Breadcrumbs";

const DatalakesListLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Datalakes" breadcrumbs={<Breadcrumbs />}>
          <Button text="Add Datalake" onClick={() => navigate("/import/datalakes/new")} type={ButtonStyle.SECONDARY} />
        </PageHeading>
        <DatalakesTable />
      </div>
    </div>
  );
};

const DatalakeFormNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Add Datalake"
          onGoBack={() => navigate("/import/datalakes")}
          breadcrumbs={<Breadcrumbs />}
        />
        <DatalakeForm />
      </div>
    </div>
  );
};

const DatalakeFormEditLayout = () => {
  const navigate = useNavigate();
  const { datalake } = useDatalakeContext();
  const initialValues = useMemo(() => {
    const values = { ...datalake } as FormValues;
    delete values.id;
    delete values.created_at;
    delete values.updated_at;
    return values;
  }, [datalake]);

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Edit Datalake"
          onGoBack={() => navigate(`/import/datalakes/${datalake.id}`)}
          breadcrumbs={<Breadcrumbs dynamicLabel={datalake.name} />}
        />
        <DatalakeForm
          datalakeId={datalake.id}
          initialValues={initialValues}
          cancelPath={`/import/datalakes/${datalake.id}`}
        />
      </div>
    </div>
  );
};

export const datalakesRouteChildren: RouteObject = {
  path: "datalakes",
  handle: {
    breadcrumb: () => ({ label: "Datalakes" }),
  },
  children: [
    {
      index: true,
      element: <DatalakesListLayout />,
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Datalake" }),
      },
      element: <DatalakeFormNewLayout />,
    },
    {
      path: ":datalakeID",
      handle: {
        breadcrumb: () => ({
          label: "Datalake Details",
          dynamic: true,
        }),
      },
      element: <DatalakePage />,
      children: [
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({ label: "Edit Datalake" }),
          },
          element: <DatalakeFormEditLayout />,
        },
        {
          element: <DatalakeDetailsPage />,
          children: [
            {
              index: true,
              element: <DatalakeDetails />,
            },
            {
              path: "providers",
              element: <DatalakeProviders />,
            },
            {
              path: "datasets",
              element: <DatalakeDatasets />,
            },
            {
              path: "connection",
              element: <DatalakeConnection />,
            },
            {
              path: "admin",
              element: <DatalakeAdmin />,
            },
          ],
        },
      ],
    },
  ],
};
